import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, Users, Globe, Lock, AlertCircle, MessageCircle } from 'lucide-react';
import { useArcheAvailability } from '@/hooks/useArcheAvailability';
import { useGuild } from '../hooks/useGuild';
import { useGuildMembers } from '../hooks/useGuildMembers';
import { useGuildActivity } from '../hooks/useGuildActivity';
import { useGuildTreasury } from '../hooks/useGuildTreasury';
import { GuildJoinButton } from '../components/GuildJoinButton';
import { GuildMemberList } from '../components/GuildMemberList';
import { GuildActivityFeed } from '../components/GuildActivityFeed';
import { GuildTreasuryCard } from '../components/GuildTreasuryCard';
import { GuildRoleBadge } from '../components/GuildRoleBadge';
import { GuildLevelBadge } from '../components/GuildLevelBadge';
import { api } from '@/lib/api';

export function GuildDetailPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { guild, loading, error, refresh, join, leave } = useGuild(guildId);
  const {
    members,
    loading: membersLoading,
    refresh: refreshMembers,
    updateRole,
    removeMember,
  } = useGuildMembers(guildId);
  const {
    activities,
    loading: activityLoading,
    hasMore,
    loadMore,
    refresh: refreshActivity,
  } = useGuildActivity(guildId);
  const {
    treasury,
    loading: treasuryLoading,
    error: treasuryError,
  } = useGuildTreasury(guild?.guildId, {
    enabled: guild?.guildType === 'earning' && !!guild?.myRole,
  });

  const chatAvailable = useArcheAvailability('arche_chat');
  const [chatChannelId, setChatChannelId] = useState<string | null>(null);
  const [chatError, setChatError] = useState(false);

  useEffect(() => {
    if (!guild?.myRole || !guildId) {
      setChatChannelId(null);
      setChatError(false);
      return;
    }

    let cancelled = false;
    api
      .getGuildChatChannel(guildId)
      .then((r) => {
        if (!cancelled) {
          setChatChannelId(r.channelId);
          setChatError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChatChannelId(null);
          setChatError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [guild?.myRole, guildId]);

  const refreshAll = async () => {
    await Promise.all([refresh(), refreshMembers(), refreshActivity()]);
  };

  const handleBan = async (userId: string, reason?: string) => {
    if (!guildId) return;
    await api.banGuildMember(guildId, userId, reason);
    await refreshAll();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !guild) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Guild not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold">{guild.name}</h1>
            {guild.visibility === 'private' ? (
              <Badge variant="outline">
                <Lock className="h-3 w-3 mr-1" />
                Private
              </Badge>
            ) : (
              <Badge variant="outline">
                <Globe className="h-3 w-3 mr-1" />
                Public
              </Badge>
            )}
            {guild.guildType === 'earning' && <Badge variant="secondary">Earning</Badge>}
            <GuildLevelBadge level={guild.level} />
          </div>
          {guild.description && <p className="text-muted-foreground">{guild.description}</p>}
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {guild.memberCount} members
            </span>
            {guild.ownerUsername && (
              <span className="text-sm">Owned by {guild.ownerUsername}</span>
            )}
            {guild.myRole && <GuildRoleBadge role={guild.myRole} />}
          </div>
        </div>
        <div className="flex gap-2">
          {guild.myRole && ['owner', 'admin'].includes(guild.myRole) && (
            <Link to={`/a/arche_guilds/${guild.guildId}/manage`}>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Manage
              </Button>
            </Link>
          )}
          <GuildJoinButton
            myRole={guild.myRole}
            visibility={guild.visibility}
            onJoin={async () => {
              await join();
              refreshMembers();
              refreshActivity();
            }}
            onLeave={async () => {
              await leave();
              refreshMembers();
              refreshActivity();
            }}
          />
        </div>
      </div>

      {/* Treasury card for earning guilds */}
      {guild.guildType === 'earning' &&
        guild.myRole &&
        (treasuryError ? (
          <Card className="border-destructive">
            <CardContent className="py-4">
              <p className="text-sm text-destructive">{treasuryError}</p>
            </CardContent>
          </Card>
        ) : (
          <GuildTreasuryCard treasury={treasury} loading={treasuryLoading} />
        ))}

      {/* Tabs */}
      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="chat" disabled={!chatAvailable || !guild.myRole}>
            Chat
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          {membersLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <GuildMemberList
              members={members}
              myRole={guild.myRole}
              onUpdateRole={async (userId, role) => {
                await updateRole(userId, role);
                refreshActivity();
              }}
              onRemove={async (userId) => {
                await removeMember(userId);
                await refresh();
                refreshActivity();
              }}
              onBan={handleBan}
            />
          )}
        </TabsContent>

        <TabsContent value="activity">
          <GuildActivityFeed
            activities={activities}
            loading={activityLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        </TabsContent>

        <TabsContent value="chat">
          {chatChannelId && chatAvailable ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-4">This guild has a dedicated chat channel.</p>
              <Link to={`/a/arche_chat/channel/${chatChannelId}`}>
                <Button>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Open Guild Chat
                </Button>
              </Link>
            </div>
          ) : (
            <p className="text-muted-foreground py-8 text-center">
              {!chatAvailable
                ? 'Chat is currently unavailable.'
                : chatError
                  ? 'Could not load guild chat. Please try again later.'
                  : 'Loading chat...'}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
